import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { teamMembers, reviews } from '@/mocks/shows';

export default function About() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-3">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Hero */}
        <section className="py-12 md:py-16 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20entertainment%20office%20workspace%20with%20large%20screen%20displays%2C%20warm%20ambient%20lighting%2C%20creative%20atmosphere%2C%20cozy%20and%20professional%20vibe%2C%20cinematic%20posters%20on%20wall%2C%20golden%20hour%20lighting&width=1600&height=500&seq=about-hero&orientation=landscape"
            alt="琪琪影院团队"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              关于琪琪影院
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
              琪琪影院成立于2020年，致力于为中国观众打造最好的追剧体验。我们汇聚电视剧全集、最新电视剧、热门电视剧，韩剧国产剧全覆盖，让每一位追剧爱好者都能在这里找到心仪的好剧。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-10 md:py-14">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    琪琪影院的诞生源于创始团队对影视文化的热爱。在多年的视频平台从业经历中，我们发现很多追剧爱好者在寻找高质量电视剧全集时面临诸多不便——资源分散、分类混乱、更新不及时。
                  </p>
                  <p>
                    于是我们决定打造一个专注于电视剧集的平台，汇聚最新电视剧和热门电视剧。从最初的几个剧集页面到现在涵盖韩剧、国产剧、美剧、日剧四大板块，琪琪影院已经服务了超过<strong>500万</strong>追剧用户，积累了<strong>10,000+</strong>部电视剧全集的资源库。
                  </p>
                  <p>
                    我们始终秉持"让好剧被看见"的理念，通过精心编辑的推荐和智能化的分类系统，帮助每一位用户快速找到值得观看的优质内容。无论是现象级热播剧还是小众口碑佳作，琪琪影院都为它们留有一席之地。
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Team%20of%20young%20Chinese%20professionals%20discussing%20in%20a%20modern%20creative%20office%2C%20warm%20lighting%2C%20collaborative%20atmosphere%2C%20large%20monitors%20showing%20film%20content%2C%20casual%20but%20professional%20style%2C%20natural%20poses%2C%20golden%20hour%20light%20through%20windows&width=800&height=500&seq=about-story&orientation=landscape"
                  alt="琪琪影院团队讨论"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                核心价值
              </h2>
              <p className="text-sm text-foreground-500 mt-1">我们坚持的追剧服务理念</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                { icon: 'ri-layout-masonry-line', title: '全面覆盖', desc: '韩剧、国产剧、美剧、日剧，电视剧全集一网打尽，满足不同口味的追剧需求。' },
                { icon: 'ri-speed-up-line', title: '极速更新', desc: '最新电视剧上线后第一时间同步，热门电视剧更新信息实时推送。' },
                { icon: 'ri-hd-line', title: '高清画质', desc: '高清在线观看体验，流畅不缓冲，让每一帧画面都成为享受。' },
                { icon: 'ri-thumb-up-line', title: '精选推荐', desc: '编辑团队精心筛选高分好剧，基于用户偏好智能推荐，让好剧不被错过。' },
              ].map((value) => (
                <div key={value.title} className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                    <i className={`${value.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-10 md:py-14">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                核心团队
              </h2>
              <p className="text-sm text-foreground-500 mt-1">热爱影视、专注追剧体验的团队</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-3xl mx-auto">
              {teamMembers.map((member) => (
                <div key={member.name} className="p-6 rounded-xl bg-background-100 border border-background-200 text-center">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-20 h-20 mx-auto rounded-full object-cover mb-4"
                  />
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-1">{member.name}</h3>
                  <p className="text-xs text-primary-500 font-medium mb-3">{member.role}</p>
                  <p className="text-sm text-foreground-500 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 mt-1">听听追剧用户对琪琪影院的真实评价</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {reviews.map((review) => (
                <div key={review.id} className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img src={review.avatar} alt={review.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-10 md:py-14">
          <div className="w-full max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                有任何问题或建议，欢迎随时联系我们
              </p>
            </div>

            <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200">
              <form
                id="about-contact-form"
                data-readdy-form
                className="space-y-4"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                  if (honeypot) {
                    const statusEl = document.getElementById('about-form-status');
                    if (statusEl) {
                      statusEl.textContent = '提交成功！我们会尽快回复您。';
                      statusEl.className = 'text-sm text-green-600 mt-3 text-center';
                    }
                    return;
                  }
                  const formData = new FormData(form);
                  try {
                    const res = await fetch('https://readdy.ai/api/form/d9n3ejcit35ii3jdlo1g', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: new URLSearchParams(formData as any).toString(),
                    });
                    const text = await res.text();
                    let parsed: any = {};
                    try { parsed = JSON.parse(text); } catch {}
                    const statusEl = document.getElementById('about-form-status');
                    if (statusEl) {
                      if (res.ok && parsed?.code === 'OK') {
                        statusEl.textContent = '提交成功！我们会尽快回复您。';
                        statusEl.className = 'text-sm text-green-600 mt-3 text-center';
                        form.reset();
                      } else {
                        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
                        statusEl.textContent = msg;
                        statusEl.className = 'text-sm text-red-500 mt-3 text-center';
                      }
                    }
                  } catch (_err) {
                    const statusEl = document.getElementById('about-form-status');
                    if (statusEl) {
                      statusEl.textContent = '网络错误，请稍后重试';
                      statusEl.className = 'text-sm text-red-500 mt-3 text-center';
                    }
                  }
                }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="about-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="about-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="about-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  />
                </div>
                <div className="honeypot-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
                <div id="about-form-status"></div>
              </form>
            </div>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-10 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@qiyicinema.com' },
                { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市朝阳区影视产业园A座' },
                { icon: 'ri-customer-service-line', title: '客服热线', value: '400-888-8888' },
              ].map((info) => (
                <div key={info.title} className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                    <i className={`${info.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-0.5">{info.title}</p>
                    <p className="text-sm font-medium text-foreground-950">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          'name': '关于琪琪影院',
          'description': '琪琪影院汇聚电视剧全集、最新电视剧、热门电视剧，韩剧国产剧全覆盖，高清在线免费看不缓冲，追剧首选平台。',
          'url': 'https://qiyicinema.com/about',
          'isPartOf': {
            '@type': 'WebSite',
            'name': '琪琪影院',
            'url': 'https://qiyicinema.com',
          },
          'mainEntity': {
            '@type': 'Organization',
            'name': '琪琪影院',
            'url': 'https://qiyicinema.com',
            'description': '琪琪影院成立于2020年，致力于为中国观众打造最好的追剧体验。',
            'foundingDate': '2020',
            'contactPoint': {
              '@type': 'ContactPoint',
              'contactType': 'customer service',
              'email': 'contact@qiyicinema.com',
              'telephone': '+86-400-888-8888',
            },
            'address': {
              '@type': 'PostalAddress',
              'addressLocality': '北京',
              'addressRegion': '朝阳区',
              'streetAddress': '影视产业园A座',
              'addressCountry': 'CN',
            },
          },
        }),
      }} />
    </div>
  );
}